import { useSliderContext, useSliderStateContext } from "@contexts/slider";
import React, { useCallback, useEffect, useMemo, useState } from 'react';

export default function useGallery<T>(data: T[], Component: Slider<T>) {
    const state = useSliderStateContext()
    const slider = useSliderContext()
    const [current, setCurrent] = useState<number>()

    useEffect(() => {
        if (state === "hidden" && current !== undefined)
            setCurrent(undefined)
    }, [state])

    const keyListener = useCallback((e: KeyboardEvent) => {
        if (current === undefined)
            return
            
        switch (e.key) {
            case "ArrowRight":
                next()
                break
            case "ArrowLeft":
                prev()
                break
        }
    }, [current, data])

    useEffect(() => {
        document.addEventListener("keydown", keyListener)
        return () => document.removeEventListener("keydown", keyListener)
    }, [keyListener])

    const next = () => {
        if (current === undefined)
            throw "Can't navigate to next nomination if none is opened."

        open(current + 1)
    }

    const prev = () => {
        if (current === undefined)
            throw "Can't navigate to previous nomination if none is opened."

        open(current - 1)
    }

    const open = (idx: number) => {
        if (idx < 0) idx = data.length - 1
        if (idx > data.length - 1) idx = 0
        setCurrent(idx)
    }

    const gallery = useMemo(() => ({
        current, prev, next, open
    }), [current, data])

    useEffect(() => {
        if (current === undefined)
            return 

        slider.setElement(<Component item={data[current]} gallery={gallery} />)
        slider.open()
    }, [current])

    return gallery
}

type Slider<T> = (props: SliderProps<T>) => JSX.Element
type SliderProps<T> = { item: T, gallery: Interfaces.Gallery }